import { ContextMenuState } from '@ardoq/context-menu';
import { AppModules } from 'appContainer/types';
import {
  type FocusedChrome,
  focusedChromeOperations,
} from 'scope/focusedChromeOperations';

export type ContextMenuStreamShape = {
  menuState: ContextMenuState;
  isViewpointMode: boolean;
  focusedChrome: FocusedChrome;
  selectedModule: AppModules;
};

const isDisabled = ({
  focusedChrome,
  selectedModule,
}: ContextMenuStreamShape): boolean =>
  focusedChromeOperations.isContextMenuDisabled({
    focusedChrome,
    selectedModule,
  });

/**
 * A disabled context menu holds no menu state. The views that build their own
 * items keep pushing them in, and nothing clears them: no `ContextMenu` mounts
 * while disabled, so its `onClose` never fires. Kept state would render
 * unprompted the moment the menu is enabled again — on whatever page the user
 * had moved to, acting on the entities they right-clicked earlier.
 *
 * Applied to the writes that can turn the menu off, which are the ones
 * {@link isDisabled} reads.
 */
const clearMenuStateWhenDisabled = (
  state: ContextMenuStreamShape
): ContextMenuStreamShape =>
  isDisabled(state) ? { ...state, menuState: null } : state;

const setMenuState = (
  state: ContextMenuStreamShape,
  menuState: ContextMenuState
): ContextMenuStreamShape =>
  clearMenuStateWhenDisabled({ ...state, menuState });

const setViewpointMode = (
  state: ContextMenuStreamShape,
  isViewpointMode: boolean
): ContextMenuStreamShape => ({ ...state, isViewpointMode });

const setFocusedChrome = (
  state: ContextMenuStreamShape,
  focusedChrome: FocusedChrome
): ContextMenuStreamShape =>
  clearMenuStateWhenDisabled({ ...state, focusedChrome });

const setSelectedModule = (
  state: ContextMenuStreamShape,
  selectedModule: AppModules
): ContextMenuStreamShape =>
  clearMenuStateWhenDisabled({ ...state, selectedModule });

export const contextMenuStateOperations = {
  isDisabled,
  setMenuState,
  setViewpointMode,
  setFocusedChrome,
  setSelectedModule,
};
